import { useState, useRef, useCallback } from "react";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Camera, Upload, X, Image as ImageIcon, Link } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";

interface ImageUploadProps {
  value?: string;
  onChange: (value: string) => void;
  onRemove?: () => void;
  maxSizeMB?: number;
  className?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function ImageUpload({
  value,
  onChange,
  onRemove,
  maxSizeMB = 5,
  className,
  label = "Imagen",
  placeholder = "Arrastra una imagen o haz clic para seleccionar",
  disabled = false,
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(async (file: File) => {
    setError(null);
    setIsLoading(true);

    // Validar tipo
    if (!file.type.startsWith("image/")) {
      setError("El archivo debe ser una imagen");
      setIsLoading(false);
      return;
    }

    // Validar tamaño
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      setError(`La imagen no puede superar ${maxSizeMB}MB`);
      setIsLoading(false);
      return;
    }

    try {
      // Comprimir y convertir a base64
      const compressed = await compressImage(file, 1200, 0.8);
      onChange(compressed);
    } catch (err) {
      setError("Error al procesar la imagen");
    } finally {
      setIsLoading(false);
    }
  }, [maxSizeMB, onChange]);

  const compressImage = (file: File, maxWidth: number, quality: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [disabled, processFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = ""; // Reset para permitir seleccionar el mismo archivo
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange(urlInput.trim());
      setUrlInput("");
    }
  };

  const handleRemove = () => {
    onChange("");
    onRemove?.();
  };

  // Si hay una imagen, mostrarla
  if (value) {
    return (
      <div className={cn("space-y-2", className)}>
        {label && <Label>{label}</Label>}
        <div className="relative rounded-lg overflow-hidden border bg-muted">
          <img
            src={value}
            alt="Preview"
            className="w-full h-48 object-contain bg-black/5"
          />
          {!disabled && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={handleRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}
      
      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload" disabled={disabled}>
            <Upload className="h-4 w-4 mr-2" />
            Subir
          </TabsTrigger>
          <TabsTrigger value="url" disabled={disabled}>
            <Link className="h-4 w-4 mr-2" />
            URL
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload" className="mt-2">
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
              isDragging && "border-primary bg-primary/5",
              disabled && "opacity-50 cursor-not-allowed",
              !disabled && "cursor-pointer hover:border-primary/50"
            )}
            onDragOver={(e) => {
              e.preventDefault();
              if (!disabled) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => !disabled && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
              disabled={disabled}
            />
            
            {isLoading ? (
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                <p className="text-sm text-muted-foreground">Procesando...</p>
              </div>
            ) : (
              <>
                <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{placeholder}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Máximo {maxSizeMB}MB
                </p>
              </>
            )}
          </div>

          {/* Botón de cámara para móviles */}
          <div className="flex gap-2 mt-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => cameraInputRef.current?.click()}
              disabled={disabled || isLoading}
            >
              <Camera className="h-4 w-4 mr-2" />
              Tomar Foto
            </Button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
              disabled={disabled}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="url" className="mt-2">
          <div className="flex gap-2">
            <Input
              placeholder="https://ejemplo.com/imagen.jpg"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              disabled={disabled}
            />
            <Button
              type="button"
              onClick={handleUrlSubmit}
              disabled={disabled || !urlInput.trim()}
            >
              Agregar
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}

// Componente para múltiples imágenes
interface MultiImageUploadProps {
  value: string[];
  onChange: (value: string[]) => void;
  maxImages?: number;
  maxSizeMB?: number;
  className?: string;
  label?: string;
  disabled?: boolean;
}

export function MultiImageUpload({
  value = [],
  onChange,
  maxImages = 5,
  maxSizeMB = 5,
  className,
  label = "Imágenes",
  disabled = false,
}: MultiImageUploadProps) {
  const handleAdd = (imageUrl: string) => {
    if (value.length < maxImages) {
      onChange([...value, imageUrl]);
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className={cn("space-y-3", className)}>
      {label && <Label>{label}</Label>}
      
      {/* Imágenes existentes */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {value.map((img, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden border">
              <img src={img} alt={`Imagen ${index + 1}`} className="w-full h-full object-cover" />
              {!disabled && (
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={() => handleRemove(index)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Agregar más */}
      {value.length < maxImages && (
        <ImageUpload
          onChange={handleAdd}
          maxSizeMB={maxSizeMB}
          label=""
          placeholder={`Agregar imagen (${value.length}/${maxImages})`}
          disabled={disabled}
        />
      )}
    </div>
  );
}

